import { useEffect, useState, useMemo } from "react";
import { Button } from "@mui/material";
import {
  FiRefreshCw,
  FiServer,
  FiFolderPlus,
  FiFileText,
  FiCheck,
  FiX,
  FiCalendar,
  FiUsers,
  FiGrid,
} from "react-icons/fi";
import {
  getAutomationFilesList_API,
  getMachinesList_API,
  automationExecute_API,
  getBoundaryCategories_API,
  fetchFieldNames_API,
} from "../../../api/controller/AutomationController";
import { executeRegressionPlan_API } from "../../../api/controller/RegressionController";
import {
  getReleaseFolderList_API,
  addRelease_API,
} from "../../../api/controller/PlanningController";
import { getTeamsList_API } from "../../../api/controller/TeamModuleController";
import {
  Entity,
  ModulePath,
  Severity,
} from "../../../constant/ApplicationVariables";
import { useApplicationContext } from "../../../context/ApplicationContext";
import { useThemeContext } from "../../../context/ThemeContext";
import useErrorHandler from "../../../api/errorHandler/useErrorHandler";

import { useLocation } from "react-router-dom";
import { HttpStatusCode } from "axios";
import { Local_StepsContentType } from "../../../constant/ApplicationJson";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  Typography,
  Box,
  alpha,
  Chip,
  IconButton,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  useTheme,
} from "@mui/material";
import { People, Info, PeopleOutline, Edit } from "@mui/icons-material";
import BoundaryMatrix from "../../dialog/BoundaryMatrix";

// =======================================================
// PLACEHOLDER MUI COMPONENTS
// =======================================================
const BoxComponent = ({ className, children }) => (
  <div className={className}>{children}</div>
);
const TypographyComponent = ({
  fontSize,
  fontWeight,
  color,
  className,
  children,
}) => {
  let styleClasses = className || "";
  if (fontWeight === "bold") styleClasses += " font-bold";
  if (color === "secondary") styleClasses += " text-blue-600";
  if (fontSize) styleClasses += " text-sm";
  return <span className={styleClasses}>{children}</span>;
};
const font = { size: { fs_4: true } };

// =======================================================
// EXECUTE MODAL COMPONENT (MAIN)
// =======================================================

export default function ExecuteModal({
  setShowExecuteModal,
  projectName,
  templateData,
  customId,
  fileId,
  folderPath,
  section,
  open,
  suite,
  onClose,
  executionType = "AUTOMATION",
  planName,
}) {
  const isRegressionMode = executionType === "REGRESSION";
  const {
    userDetail,
    handleSnackbar,
    activeProject,
    currentExecutionTestCaseData,
  } = useApplicationContext();
  const { isDarkMode } = useThemeContext();
  const errorHandler = useErrorHandler();
  const pathName = useLocation()?.pathname;
  const theme = useTheme();

  // Helper function to get button text color based on theme
  const getButtonTextColor = () => {
    return theme.palette.mode === "light" ? "#ffffff" : "#000000";
  };

  const [machines, setMachines] = useState([]);
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [filesLoading, setFilesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [releaseList, setReleaseList] = useState([]);
  const [selectedRelease, setSelectedRelease] = useState(null);
  const [releaseLoading, setReleaseLoading] = useState(false);
  const [isCreatingRelease, setIsCreatingRelease] = useState(false);
  const [newReleaseName, setNewReleaseName] = useState("");
  const [newReleaseDescription, setNewReleaseDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [automationFilesData, setAutomationFilesData] = useState({});

  // Visibility State
  const [visibility, setVisibility] = useState("PUBLIC");
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [teams, setTeams] = useState([]);
  const [openTeamDialog, setOpenTeamDialog] = useState(false);
  const [tempSelectedTeams, setTempSelectedTeams] = useState([]);

  // Boundary Conditions State - Dynamically from API
  const [enableBoundaryConditions, setEnableBoundaryConditions] = useState(false);
  const [enableRegressionBoundary, setEnableRegressionBoundary] = useState(false);
  const [selectedMatrixAutomation, setSelectedMatrixAutomation] = useState({});
  const [selectedMatrixRegression, setSelectedMatrixRegression] = useState({});

  const [boundaryCategories, setBoundaryCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [fieldNames, setFieldNames] = useState([]);
  const [fieldNamesLoading, setFieldNamesLoading] = useState(false);

  const fileTypeList = [
    Local_StepsContentType.script,
    Local_StepsContentType.element,
    Local_StepsContentType.application,
    Local_StepsContentType.testData,
  ];

  const currentMatrix = isRegressionMode ? selectedMatrixRegression : selectedMatrixAutomation;
  const setCurrentMatrix = isRegressionMode ? setSelectedMatrixRegression : setSelectedMatrixAutomation;
  const enableBoundaryForCurrentMode = isRegressionMode ? enableRegressionBoundary : enableBoundaryConditions;

  // Add this useEffect to debug and ensure planName is available
  useEffect(() => {
    if (isRegressionMode) {
      console.log("Regression Mode - Plan Name:", planName);
      console.log("Project Name:", activeProject?.name);
      
      // If planName is missing, try to get it from props or context
      if (!planName && currentExecutionTestCaseData?.planName) {
        console.log("Found planName in currentExecutionTestCaseData:", currentExecutionTestCaseData.planName);
        // You might want to set it to a state variable or pass it differently
      }
    }
  }, [isRegressionMode, planName, activeProject, currentExecutionTestCaseData]);

  // Fix the loadFieldNames function for regression mode
  const loadFieldNames = async () => {
    try {
      setFieldNamesLoading(true);
      
      console.log("=== loadFieldNames called ===");
      console.log("isRegressionMode:", isRegressionMode);
      console.log("planName prop:", planName);
      console.log("suite?.name:", suite?.name);
      console.log("activeProject?.name:", activeProject?.name);
      
      let requestData = {};

      if (isRegressionMode) {
        // Try to get effective plan name from multiple sources
        let effectivePlanName = planName;
        if (!effectivePlanName && suite?.name) {
          effectivePlanName = suite.name;
          console.log("Using suite.name as planName:", effectivePlanName);
        }
        if (!effectivePlanName && currentExecutionTestCaseData?.planName) {
          effectivePlanName = currentExecutionTestCaseData.planName;
          console.log("Using currentExecutionTestCaseData.planName:", effectivePlanName);
        }
        if (!effectivePlanName && templateData?.name) {
          effectivePlanName = templateData.name;
          console.log("Using templateData.name as planName:", effectivePlanName);
        }
        
        if (!effectivePlanName || !activeProject?.name) {
          console.error("Missing required data for regression field loading", { 
            planNameProp: planName,
            effectivePlanName,
            suiteName: suite?.name,
            projectName: activeProject?.name 
          });
          
          handleSnackbar(
            "Plan name is required to load boundary configurations. Please ensure the plan is properly selected and try again.",
            Severity.warning
          );
          setFieldNames([]);
          setFieldNamesLoading(false);
          return;
        }
        
        requestData = {
          planName: effectivePlanName,
          projectName: activeProject.name,
        };
      } else {
        // Automation mode
        const scriptFile = automationFilesData[Local_StepsContentType.script]?.value;
        
        if (!scriptFile?.customId || !activeProject?.name || !scriptFile?.folderPath) {
          console.error("Missing required automation data");
          handleSnackbar(
            "Automation script file data is incomplete. Please ensure a script file is selected.",
            Severity.warning
          );
          setFieldNames([]);
          setFieldNamesLoading(false);
          return;
        }

        requestData = {
          customId: scriptFile.customId,
          projectName: activeProject.name,
          folderPath: scriptFile.folderPath,
          section: scriptFile.section || "script",
          fileId: scriptFile.fileId,
        };
      }

      console.log("Making API call with requestData:", requestData);
      
      const res = await fetchFieldNames_API(requestData);
      
      let fieldNamesArray = null;
      
      if (res?.fieldNames && Array.isArray(res.fieldNames)) {
        fieldNamesArray = res.fieldNames;
      } else if (res?.data?.fieldNames && Array.isArray(res.data.fieldNames)) {
        fieldNamesArray = res.data.fieldNames;
      } else if (res?.data && Array.isArray(res.data)) {
        fieldNamesArray = res.data;
      } else if (Array.isArray(res)) {
        fieldNamesArray = res;
      }

      if (fieldNamesArray && fieldNamesArray.length > 0) {
        console.log("Field names loaded successfully:", fieldNamesArray);
        setFieldNames(fieldNamesArray);
        if (isRegressionMode) {
          const planUsed = planName || suite?.name;
          handleSnackbar(`Loaded ${fieldNamesArray.length} boundary configured field(s) from plan "${planUsed}"`, Severity.success);
        } else {
          handleSnackbar(`Loaded ${fieldNamesArray.length} field(s) from script`, Severity.success);
        }
      } else {
        console.warn("No valid fieldNames in API response");
        setFieldNames([]);
        
        if (isRegressionMode) {
          const planUsed = planName || suite?.name;
          if (res?.message) {
            handleSnackbar(res.message, Severity.info);
          } else {
            handleSnackbar(`No boundary conditions configured for plan "${planUsed}". Configure boundary conditions in the plan first by clicking the settings icon next to each test case.`, Severity.info);
          }
        } else {
          if (res?.message) {
            handleSnackbar(res.message, Severity.warning);
          } else {
            handleSnackbar("No fields available. Please ensure script file has Scenario Outline with Examples.", Severity.warning);
          }
        }
      }
    } catch (err) {
      console.error("Error fetching field names:", err);
      
      if (isRegressionMode) {
        const planUsed = planName || suite?.name;
        // handleSnackbar(
        //   `Failed to load boundary configurations from plan "${planUsed}". Please check if plan has valid boundary configurations.`,
        //   Severity.error
        // );
      } else {
        if (err.response?.data?.message) {
          handleSnackbar(err.response.data.message, Severity.error);
        } else if (err.message) {
          handleSnackbar(`Failed to load fields: ${err.message}`, Severity.error);
        } else {
          handleSnackbar("Failed to load field names", Severity.error);
        }
      }
      
      setFieldNames([]);
    } finally {
      setFieldNamesLoading(false);
    }
  };

const handleRegressionExecution = async () => {
  // Validate required fields
  if (!selectedRelease) {
    handleSnackbar("Please select a release.", Severity.warning);
    return;
  }

  if (!selectedMachine) {
    handleSnackbar("Please select a machine.", Severity.warning);
    return;
  }

  if (visibility === "TEAM" && selectedTeams.length === 0) {
    handleSnackbar(
      "Please select at least one team when visibility is set to TEAM.",
      Severity.warning
    );
    return;
  }

  // Get effective plan name from multiple sources
  let effectivePlanName = planName;
  
  if (!effectivePlanName && suite?.name) {
    effectivePlanName = suite.name;
    console.log("Using planName from suite.name:", effectivePlanName);
  }
  
  if (!effectivePlanName && currentExecutionTestCaseData?.planName) {
    effectivePlanName = currentExecutionTestCaseData.planName;
    console.log("Using planName from currentExecutionTestCaseData:", effectivePlanName);
  }
  
  if (!effectivePlanName && templateData?.name) {
    effectivePlanName = templateData.name;
    console.log("Using planName from templateData.name:", effectivePlanName);
  }

  if (!effectivePlanName) {
    console.error("No plan name found");
    handleSnackbar(
      "Plan name is required for regression execution. Please ensure the plan is properly selected.",
      Severity.warning
    );
    return;
  }

  setLoading(true);

  try {
    const payload = {
      planName: effectivePlanName,
      projectName: activeProject?.name,
      releaseName: selectedRelease.releaseName,
      machineName: selectedMachine.machineName,
      boundaryMode: enableRegressionBoundary,
    };

    console.log("Regression execution payload:", payload);

    const response = await executeRegressionPlan_API(payload);

    console.log("Regression execution response:", response);

    // Check for successful response
    if (response && (response.status === HttpStatusCode.Ok || response.status === 200)) {
      const executionData = response.data;
      // Handle different response structures
      let successMessage = "Regression execution started successfully!";
      
      if (executionData?.message) {
        successMessage = executionData.message;
      } else if (executionData?.automationDetails?.executionName) {
        successMessage = `Execution started: ${executionData.automationDetails.executionName}`;
      } else if (executionData?.executionName) {
        successMessage = `Execution started: ${executionData.executionName}`;
      } else if (typeof executionData === 'string') {
        successMessage = executionData;
      }
      
      handleSnackbar(successMessage, Severity.success);
      
      // Close modal on success
      if (setShowExecuteModal) {
        setShowExecuteModal({ visible: false });
      }
      if (onClose) {
        onClose();
      }
    } else {
      // Handle error response
      const errorMessage = response?.data?.message || 
                          response?.data?.detail || 
                          response?.message || 
                          "Failed to execute regression plan";
      throw new Error(errorMessage);
    }
  } catch (error) {
    console.error("Regression execution error:", error);
    
    // Check if error response actually contains success data
    if (error.response?.data?.automationDetails?.executionName) {
      handleSnackbar(
        `Execution started: ${error.response.data.automationDetails.executionName}`,
        Severity.success
      );
      if (setShowExecuteModal) {
        setShowExecuteModal({ visible: false });
      }
      if (onClose) {
        onClose();
      }
    } else {
      handleSnackbar(
        error.response?.data?.message || 
        error.response?.data?.detail || 
        error.message || 
        "Failed to execute regression plan",
        Severity.error
      );
    }
  } finally {
    setLoading(false);
  }
};

  // SINGLE useEffect for loading field names
  useEffect(() => {
    if (isRegressionMode) {
      // Try to get effective plan name from multiple sources
      let effectivePlanName = planName;
      if (!effectivePlanName && suite?.name) {
        effectivePlanName = suite.name;
      }
      if (!effectivePlanName && currentExecutionTestCaseData?.planName) {
        effectivePlanName = currentExecutionTestCaseData.planName;
      }
      if (!effectivePlanName && templateData?.name) {
        effectivePlanName = templateData.name;
      }
      
      // Load field names when effective plan name is available
      if (effectivePlanName && activeProject?.name) {
        console.log("Loading field names for regression with plan:", effectivePlanName);
        loadFieldNames();
      } else {
        console.log("Waiting for plan name to load field names", { 
          planNameProp: planName,
          effectivePlanName, 
          projectName: activeProject?.name,
          suiteName: suite?.name,
          templateDataName: templateData?.name
        });
        
        // Clear field names if no plan name is available
        if (fieldNames.length > 0) {
          setFieldNames([]);
        }
      }
    } else {
      // Automation mode
      const scriptFile = automationFilesData[Local_StepsContentType.script]?.value;
      if (scriptFile?.customId && scriptFile?.folderPath) {
        loadFieldNames();
      }
    }
  }, [
    isRegressionMode,
    planName,
    suite?.name,
    templateData?.name,
    currentExecutionTestCaseData?.planName,
    activeProject?.name,
    automationFilesData[Local_StepsContentType.script]?.value
  ]);
  
  // Fetch Teams List
  const fetchTeams = async () => {
    try {
      const response = await getTeamsList_API();
      if (Array.isArray(response?.data)) {
        setTeams(response.data);
      }
    } catch (error) {
      console.error("Error fetching teams:", error);
    }
  };

  // Fetch Boundary Categories from API
  const fetchBoundaryCategories = async () => {
    try {
      setCategoriesLoading(true);
      const response = await getBoundaryCategories_API();

      if (response && response.status === HttpStatusCode.Ok) {
        const categories = response.data || [];
        const categoryNames = categories.map((cat) => cat.name || cat);
        setBoundaryCategories(categoryNames);

        if (categoryNames.length === 0) {
          handleSnackbar(
            "No boundary categories available from API.",
            Severity.info,
          );
        }
      } else {
        console.error("Failed to fetch boundary categories:", response);
        setBoundaryCategories([]);
        handleSnackbar(
          "Failed to load boundary categories from API. Please check your connection.",
          Severity.error,
        );
      }
    } catch (err) {
      console.error("Error fetching boundary categories:", err);
      setBoundaryCategories([]);
      handleSnackbar(
        "Failed to load boundary categories. Please check your connection.",
        Severity.error,
      );
    } finally {
      setCategoriesLoading(false);
    }
  };

  const fetchReleaseList = async () => {
    try {
      setReleaseLoading(true);
      const query = new URLSearchParams({
        name: activeProject?.name,
      }).toString();
      const response = await getReleaseFolderList_API(query);
      const releases = response?.data || [];
      setReleaseList(releases);
      if (releases.length === 1) setSelectedRelease(releases[0]);
    } catch (err) {
      setReleaseList([]);
    } finally {
      setReleaseLoading(false);
    }
  };

  const handleGetAutomationFilesList = async (fileType) => {
    try {
      const body = {
        projectName: activeProject?.name,
        folderPath: templateData?.folderPath,
        customId: templateData?.customId,
        fileType,
        section:
          pathName === ModulePath.requirements
            ? Entity.userStory
            : pathName === ModulePath.useCase
              ? Entity.useCase
              : Entity.testCase,
      };
      const response = await getAutomationFilesList_API(body);
      if (response) errorHandler(response, setLoading);
      if (response?.status === HttpStatusCode.Ok) {
        const data = response.data;
        const allAvailable = Object.values(data).flat();
        let selected = allAvailable.find((item) => item?.primary);
        if (!selected) selected = allAvailable[0] || null;
        setAutomationFilesData((prev) => ({
          ...prev,
          [fileType]: { value: selected, data },
        }));
      }
    } catch (error) {
      handleSnackbar(String(error), Severity.error);
    }
  };

  const fetchAllAutomationFiles = async () => {
    try {
      setFilesLoading(true);
      await Promise.all(
        fileTypeList.map((t) => handleGetAutomationFilesList(t)),
      );
    } finally {
      setFilesLoading(false);
    }
  };

  const fetchMachines = async () => {
    setLoading(true);
    try {
      const response = await getMachinesList_API({ email: userDetail?.email });
      const list = response?.data || [];
      setMachines(list);
      if (list.length === 1) setSelectedMachine(list[0]);
    } catch (error) {
      setMachines([]);
      setSelectedMachine(null);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRelease = async () => {
    if (!newReleaseName.trim()) {
      handleSnackbar("Release name cannot be empty.", Severity.warning);
      return;
    }
    if (!startDate) {
      handleSnackbar("Please select start date.", Severity.warning);
      return;
    }
    if (releaseList.some((r) => r.releaseName === newReleaseName.trim())) {
      handleSnackbar("Release name already exists.", Severity.warning);
      return;
    }

    try {
      setLoading(true);

      const startDateISO = new Date(startDate).toISOString();
      const endDateISO = new Date(endDate).toISOString();

      const releasePayload = {
        name: newReleaseName.trim(),
        description: newReleaseDescription.trim() || "No description provided",
        startDate: startDateISO,
        endDate: endDateISO,
        projectName: activeProject?.name,
      };

      const response = await addRelease_API(releasePayload);

      if (response && response.status === HttpStatusCode.Ok) {
        const newRelease = {
          releaseName: newReleaseName.trim(),
          ...response.data,
        };

        setReleaseList((prev) => [...prev, newRelease]);
        setSelectedRelease(newRelease);
        setNewReleaseName("");
        setNewReleaseDescription("");
        setStartDate("");
        setEndDate("");
        setIsCreatingRelease(false);

        handleSnackbar(
          `Release '${newRelease.releaseName}' created successfully!`,
          Severity.success,
        );
      } else {
        throw new Error(response?.data?.message || "Failed to create release");
      }
    } catch (error) {
      console.error("Error creating release:", error);
      handleSnackbar(String(error), Severity.error);
    } finally {
      setLoading(false);
    }
  };

  const resetReleaseForm = () => {
    setNewReleaseName("");
    setNewReleaseDescription("");
    setStartDate("");
    setEndDate("");
    setIsCreatingRelease(false);
  };

  // Team Management Functions
  const handleOpenTeamDialog = () => {
    setTempSelectedTeams([...selectedTeams]);
    setOpenTeamDialog(true);
  };

  const handleTeamToggle = (teamName) => {
    setTempSelectedTeams((prev) => {
      if (prev.includes(teamName)) {
        return prev.filter((t) => t !== teamName);
      } else {
        return [...prev, teamName];
      }
    });
  };

  const handleTeamSelectionConfirm = () => {
    setVisibility("TEAM");
    setSelectedTeams(tempSelectedTeams);
    setOpenTeamDialog(false);
  };

  const handleTeamSelectionCancel = () => {
    setOpenTeamDialog(false);
    setTempSelectedTeams([...selectedTeams]);
  };

  const handleEditTeams = () => {
    handleOpenTeamDialog();
  };

  // Handle visibility change
  const handleVisibilityChange = (event) => {
    const newVisibility = event.target.value;

    if (newVisibility === "TEAM") {
      setTempSelectedTeams([...selectedTeams]);
      setOpenTeamDialog(true);
    } else {
      setVisibility(newVisibility);
      if (newVisibility !== "TEAM") {
        setSelectedTeams([]);
      }
    }
  };

  // Main useEffect for loading initial data
  useEffect(() => {
    fetchReleaseList();
    fetchMachines();
    fetchBoundaryCategories();
    fetchTeams();
  }, [activeProject]);

  // Separate useEffect for loading files based on mode
  useEffect(() => {
    if (!isRegressionMode) {
      fetchAllAutomationFiles();
    }
  }, [isRegressionMode, templateData, activeProject]);

  const handleExecution = async () => {
    try {
      if (isRegressionMode) {
        await handleRegressionExecution();
      } else {
        await handleAutomationExecution();
      }
    } catch (err) {
      handleSnackbar("Execution failed", Severity.error);
    }
  };

  // Calculate if the execute button should be disabled
  const isExecuteDisabled = useMemo(() => {
    // Basic validations
    if (!selectedRelease || !selectedMachine) return true;
    if (visibility === "TEAM" && selectedTeams.length === 0) return true;
    
    // Regression mode - only need release and machine
    if (isRegressionMode) {
      return false;
    }
    
    // Automation mode - check boundary selections if enabled
    if (enableBoundaryConditions && Object.values(currentMatrix).flat().length === 0) {
      return true;
    }
    
    return false;
  }, [selectedRelease, selectedMachine, visibility, selectedTeams, isRegressionMode, enableBoundaryConditions, currentMatrix]);

const handleAutomationExecution = async () => {
  const finalMatrix = currentMatrix;
  const finalCategories = Object.values(finalMatrix).flat();

  if (!selectedRelease) {
    handleSnackbar("Please select a release.", Severity.warning);
    return;
  }
  if (!selectedMachine) {
    handleSnackbar("Please select a machine.", Severity.warning);
    return;
  }
  
  if (enableBoundaryConditions && finalCategories.length === 0) {
    handleSnackbar(
      "Please select at least one boundary category.",
      Severity.warning,
    );
    return;
  }
  
  if (visibility === "TEAM" && selectedTeams.length === 0) {
    handleSnackbar(
      "Please select at least one team when visibility is set to TEAM.",
      Severity.warning,
    );
    return;
  }

  setLoading(true);

  try {
    const typeToKeyMap = {
      [Local_StepsContentType.element]: "elementFile",
      [Local_StepsContentType.application]: "applicationFile",
      [Local_StepsContentType.testData]: "testDataFile",
      [Local_StepsContentType.script]: "scriptFile",
    };

    const filePayload = {};
    let mainCustomId = currentExecutionTestCaseData?.customId;

    fileTypeList.forEach((type) => {
      const selectedFile = automationFilesData[type]?.value || null;
      filePayload[typeToKeyMap[type]] = selectedFile
        ? {
            customId: selectedFile.customId,
            folderPath: selectedFile.folderPath,
            fileId: selectedFile.fileId,
            section: selectedFile.section,
          }
        : null;

      if (
        type === Local_StepsContentType.script &&
        selectedFile?.customId &&
        !mainCustomId
      ) {
        mainCustomId = selectedFile.customId;
      }
    });

    if (!mainCustomId) {
      handleSnackbar(
        "Custom ID not found. Please ensure script file is loaded.",
        Severity.error,
      );
      setLoading(false);
      return;
    }

    const transformFieldCategories = (matrix) => {
      if (!matrix || typeof matrix !== 'object') return [];
      
      return Object.entries(matrix)
        .filter(([_, categories]) => categories && categories.length > 0)
        .map(([fieldName, categories]) => ({
          fieldName: fieldName,
          categories: categories
        }));
    };

    const body = {
      ...filePayload,
      machineName: selectedMachine.machineName,
      projectName: activeProject?.name,
      releaseName: selectedRelease.releaseName,
      customId: mainCustomId,
      folderPath: templateData?.folderPath,
      visibility: visibility,
      teamNames: visibility === "TEAM" ? selectedTeams : [],
      boundaryExecution: enableBoundaryConditions,
    };

    if (enableBoundaryConditions) {
      const transformedSelections = transformFieldCategories(finalMatrix);
      if (transformedSelections.length > 0) {
        body.fieldCategorySelections = transformedSelections;
      }
    }

    console.log("Request payload:", JSON.stringify(body, null, 2));

    const response = await automationExecute_API(body);
    
    console.log("Automation execution response:", response);
    
    // Check for successful response
    if (response && (response.status === HttpStatusCode.Ok || response.status === 200)) {
      const executionData = response.data;
      // Handle different response structures
      let successMessage = "Execution successful!";
      
      if (executionData?.message) {
        successMessage = executionData.message;
      } else if (executionData?.automationDetails?.executionName) {
        successMessage = `Execution started: ${executionData.automationDetails.executionName}`;
      } else if (executionData?.executionName) {
        successMessage = `Execution started: ${executionData.executionName}`;
      } else if (typeof executionData === 'string') {
        successMessage = executionData;
      }
      
      handleSnackbar(successMessage, Severity.success);
      
      if (setShowExecuteModal) {
        setShowExecuteModal({ visible: false });
      }
      if (onClose) {
        onClose();
      }
    } else {
      console.error("API Error Response:", response);
      const errorMessage = response?.data?.message || 
                          response?.data?.detail || 
                          response?.message || 
                          "Execution failed";
      handleSnackbar(errorMessage, Severity.error);
    }
  } catch (error) {
    console.error("Execution error details:", error);
    console.error("Error response:", error.response);
    console.error("Error data:", error.response?.data);
    
    // Check if error response actually contains success data
    if (error.response?.data?.automationDetails?.executionName) {
      handleSnackbar(
        `Execution started: ${error.response.data.automationDetails.executionName}`,
        Severity.success
      );
      if (setShowExecuteModal) {
        setShowExecuteModal({ visible: false });
      }
      if (onClose) {
        onClose();
      }
    } else if (error.response?.data?.executionName) {
      handleSnackbar(
        `Execution started: ${error.response.data.executionName}`,
        Severity.success
      );
      if (setShowExecuteModal) {
        setShowExecuteModal({ visible: false });
      }
      if (onClose) {
        onClose();
      }
    } else {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.detail || 
                          error.message || 
                          "Execution failed";
      handleSnackbar(errorMessage, Severity.error);
    }
  } finally {
    setTimeout(() => setLoading(false), 800);
  }
};

  return (
    <div
      className={`fixed inset-0 flex justify-center items-center z-[9999] p-4 ${isDarkMode ? "bg-black bg-opacity-70" : "bg-black bg-opacity-50"}`}
    >
      <div
        className={`rounded-xl shadow-2xl w-full max-w-6xl flex flex-col max-h-[90vh] overflow-hidden ${isDarkMode ? "bg-[#1f1f1f] text-white" : "bg-white text-black"}`}
      >
        {/* MODAL HEADER */}
        <div
          className={`p-4 border-b flex justify-between items-start sticky top-0 z-20 ${isDarkMode ? "bg-[#1f1f1f] border-gray-700" : "bg-white border-gray-200"}`}
        >
          <BoxComponent className="flex flex-col ">
            <BoxComponent className="flex-1 flex items-center gap-2 ">
              <TypographyComponent fontSize={font.size.fs_4} fontWeight="bold">
                {isRegressionMode
                  ? "Regression Configuration -"
                  : "Automation Configuration -"}
              </TypographyComponent>
              <TypographyComponent
                fontSize={font.size.fs_4}
                color="primary"
                className=" "
              >
                {isRegressionMode ? planName : (currentExecutionTestCaseData?.customId || "EXECUTIONS")}
              </TypographyComponent>
            </BoxComponent>
            <TypographyComponent
              className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-500"}`}
            >
              {isRegressionMode
                ? "Configure regression execution and boundary conditions"
                : "Configure automation execution settings and boundary conditions"}
            </TypographyComponent>
          </BoxComponent>
          <button
            onClick={() => {
              if (setShowExecuteModal) {
                setShowExecuteModal({ visible: false });
              }
              if (onClose) {
                onClose();
              }
            }}
            className={`text-xl p-1 rounded-full transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white hover:bg-gray-700"
                : "text-gray-500 hover:text-gray-800 hover:bg-gray-100"
            }`}
          >
            ✖
          </button>
        </div>

        {/* MODAL BODY */}
        <div className="p-6 overflow-y-auto space-y-6">
          {/* RELEASE SELECTION */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              className={`border rounded-lg p-4  ${isDarkMode ? "border-gray-700 bg-[#2b2b2b]" : "border-gray-200 bg-gray-50"}`}
            >
              <h3
                className={`flex items-center text-base font-semibold mb-3 ${isDarkMode ? "text-white" : "text-gray-700"}`}
              >
                <FiFolderPlus
                  className="mr-2"
                  style={{ color: theme.palette.primary.main }}
                />{" "}
                Select Target Release
              </h3>

              <div className="flex gap-3 items-center">
                {isCreatingRelease ? (
                  <div className="w-full space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label
                          className={`block text-xs font-medium mb-1 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                        >
                          Release Name *
                        </label>
                        <input
                          type="text"
                          value={newReleaseName}
                          onChange={(e) => setNewReleaseName(e.target.value)}
                          placeholder="e.g., Release-v1.0"
                          className={`border p-2 rounded-lg w-full focus:outline-none text-sm ${isDarkMode ? "border-gray-600 bg-[#3a3a3a] text-white" : "border-gray-300 bg-white text-black"}`}
                          style={{
                            borderColor: theme.palette.divider,
                          }}
                          onFocus={(e) => {
                            e.target.style.outline = `2px solid ${theme.palette.primary.main}`;
                            e.target.style.outlineOffset = "1px";
                          }}
                          onBlur={(e) => {
                            e.target.style.outline = "none";
                          }}
                        />
                      </div>
                      <div>
                        <label
                          className={`block text-xs font-medium mb-1 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                        >
                          Description
                        </label>
                        <input
                          type="text"
                          value={newReleaseDescription}
                          onChange={(e) =>
                            setNewReleaseDescription(e.target.value)
                          }
                          placeholder="Brief description"
                          className={`border p-2 rounded-lg w-full focus:outline-none text-sm ${isDarkMode ? "border-gray-600 bg-[#3a3a3a] text-white" : "border-gray-300 bg-white text-black"}`}
                          style={{
                            borderColor: theme.palette.divider,
                          }}
                          onFocus={(e) => {
                            e.target.style.outline = `2px solid ${theme.palette.primary.main}`;
                            e.target.style.outlineOffset = "1px";
                          }}
                          onBlur={(e) => {
                            e.target.style.outline = "none";
                          }}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label
                          className={`block text-xs font-medium mb-1 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                        >
                          Start Date *
                        </label>
                        <input
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          className={`border p-2 rounded-lg w-full focus:outline-none text-sm ${isDarkMode ? "border-gray-600 bg-[#3a3a3a] text-white" : "border-gray-300 bg-white text-black"}`}
                          style={{
                            borderColor: theme.palette.divider,
                          }}
                          onFocus={(e) => {
                            e.target.style.outline = `2px solid ${theme.palette.primary.main}`;
                            e.target.style.outlineOffset = "1px";
                          }}
                          onBlur={(e) => {
                            e.target.style.outline = "none";
                          }}
                        />
                      </div>
                      <div>
                        <label
                          className={`block text-xs font-medium mb-1 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                        >
                          End Date
                        </label>
                        <input
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          className={`border p-2 rounded-lg w-full focus:outline-none text-sm ${isDarkMode ? "border-gray-600 bg-[#3a3a3a] text-white" : "border-gray-300 bg-white text-black"}`}
                          style={{
                            borderColor: theme.palette.divider,
                          }}
                          onFocus={(e) => {
                            e.target.style.outline = `2px solid ${theme.palette.primary.main}`;
                            e.target.style.outlineOffset = "1px";
                          }}
                          onBlur={(e) => {
                            e.target.style.outline = "none";
                          }}
                        />
                      </div>
                    </div>

                    <div className="flex gap-2 justify-end">
                      <Button
                        onClick={handleAddRelease}
                        disabled={
                          !newReleaseName.trim() || !startDate || loading
                        }
                        variant="contained"
                        size="medium"
                        sx={{
                          backgroundColor: theme.palette.primary.main,
                          color: getButtonTextColor(),
                          textTransform: "none",
                          fontWeight: 500,
                          borderRadius: "10px",
                          "&:hover": {
                            backgroundColor: theme.palette.primary.dark,
                          },
                          "&.Mui-disabled": {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.3,
                            ),
                            color: alpha(getButtonTextColor(), 0.5),
                          },
                        }}
                      >
                        <FiCheck className="mr-1.5" />
                        {loading ? "Creating..." : "Create Release"}
                      </Button>
                      <Button
                        onClick={resetReleaseForm}
                        disabled={loading}
                        variant="outlined"
                        size="medium"
                        sx={{
                          borderColor: theme.palette.divider,
                          color: theme.palette.text.primary,
                          textTransform: "none",
                          fontWeight: 500,
                          borderRadius: "10px",
                          "&:hover": {
                            borderColor: theme.palette.primary.main,
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.05,
                            ),
                          },
                        }}
                      >
                        <FiX className="mr-1.5" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : releaseLoading ? (
                  <p
                    className={`animate-pulse p-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-400"}`}
                  >
                    Loading releases...
                  </p>
                ) : (
                  <>
                    <select
                      className={`border p-2 rounded-lg w-70 focus:outline-none transition-shadow text-sm ${
                        isDarkMode
                          ? "border-gray-600 bg-[#3a3a3a] text-white"
                          : "border-gray-300 bg-white text-black"
                      }`}
                      style={{
                        borderColor: theme.palette.divider,
                      }}
                      onFocus={(e) => {
                        e.target.style.outline = `2px solid ${theme.palette.primary.main}`;
                        e.target.style.outlineOffset = "1px";
                      }}
                      onBlur={(e) => {
                        e.target.style.outline = "none";
                      }}
                      value={selectedRelease?.releaseName || ""}
                      onChange={(e) => {
                        const found = releaseList.find(
                          (r) => r.releaseName === e.target.value,
                        );
                        setSelectedRelease(found || null);
                      }}
                    >
                      <option value="" disabled>
                        -- Select Release --
                      </option>
                      {releaseList.map((r) => (
                        <option key={r.releaseName} value={r.releaseName}>
                          {r.releaseName}
                        </option>
                      ))}
                    </select>

                    <Button
                      startIcon={<FiCalendar />}
                      onClick={() => setIsCreatingRelease(true)}
                      variant="contained"
                      sx={{
                        borderRadius: "10px",
                        textTransform: "none",
                        fontWeight: 600,
                        height: 40,
                        px: 3,
                        backgroundColor: theme.palette.primary.main,
                        color: getButtonTextColor(),
                        "&:hover": {
                          backgroundColor: theme.palette.primary.dark,
                        },
                      }}
                    >
                      Create New
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* VISIBILITY SETTINGS */}
            <div
              className={`border rounded-lg p-4 ${isDarkMode ? "border-gray-700 bg-[#2b2b2b]" : "border-gray-200 bg-gray-50"}`}
            >
              <h3
                className={`flex items-center text-base font-semibold mb-3 ${isDarkMode ? "text-white" : "text-gray-700"}`}
              >
                <FiUsers
                  className="mr-2"
                  style={{ color: theme.palette.primary.main }}
                />{" "}
                Visibility Settings
              </h3>

              <div className="space-y-4">
                <FormControl fullWidth size="small">
                  <InputLabel
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: "0.7rem",
                    }}
                  >
                    Execution Visibility
                  </InputLabel>
                  <Select
                    value={visibility}
                    onChange={handleVisibilityChange}
                    label="Execution Visibility"
                    sx={{
                      backgroundColor: isDarkMode ? "#3a3a3a" : "white",
                      color: theme.palette.text.primary,
                      borderRadius: "0.5rem",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: theme.palette.divider,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: theme.palette.primary.main,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    <MenuItem value="PUBLIC">
                      Public (Everyone can view)
                    </MenuItem>
                    <MenuItem value="PRIVATE">Private (Only me)</MenuItem>
                    <MenuItem value="TEAM">Team (Specific teams)</MenuItem>
                  </Select>
                </FormControl>

                {visibility === "TEAM" && selectedTeams.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.5,
                      mt: 1,
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: "0.75rem",
                      }}
                    >
                      Selected teams:
                    </Typography>
                    {selectedTeams.map((team, index) => (
                      <Chip
                        key={index}
                        label={team}
                        size="small"
                        onDelete={() => {
                          setSelectedTeams(
                            selectedTeams.filter((t) => t !== team),
                          );
                        }}
                        sx={{
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.1,
                          ),
                          color: theme.palette.primary.main,
                          fontSize: "0.75rem",
                          height: "24px",
                          "& .MuiChip-deleteIcon": {
                            color: theme.palette.primary.main,
                            fontSize: 14,
                          },
                        }}
                      />
                    ))}
                    <Tooltip title="Edit teams">
                      <IconButton
                        size="small"
                        onClick={handleEditTeams}
                        sx={{
                          width: 24,
                          height: 24,
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.1,
                          ),
                        }}
                      >
                        <Edit
                          sx={{
                            fontSize: 14,
                            color: theme.palette.primary.main,
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}

                {visibility === "TEAM" && selectedTeams.length === 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.warning.main,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mt: 1,
                      fontSize: "0.75rem",
                    }}
                  >
                    <Info sx={{ fontSize: 14 }} />
                    No teams selected. Click edit to add teams.
                  </Typography>
                )}
              </div>
            </div>
          </div>

          {/* Team Selection Dialog */}
          <Dialog
            open={openTeamDialog}
            onClose={handleTeamSelectionCancel}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 2,
                backgroundColor: theme.palette.background.paper,
              },
            }}
          >
            <DialogTitle
              sx={{
                borderBottom: `1px solid ${theme.palette.divider}`,
                py: 2.5,
                px: 3,
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <People
                  sx={{ fontSize: 18, color: theme.palette.primary.main }}
                />
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{ color: theme.palette.text.primary }}
                >
                  Select Teams
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  Choose teams that will have access to this execution
                </Typography>
              </Box>
            </DialogTitle>

            <DialogContent sx={{ py: 3, px: 3 }}>
              <Typography
                variant="body2"
                sx={{
                  mb: 2,
                  color: theme.palette.text.secondary,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  fontSize: "0.875rem",
                }}
              >
                <Info sx={{ fontSize: 16 }} />
                Select one or more teams from the list below:
              </Typography>

              <List sx={{ maxHeight: 400, overflowY: "auto" }}>
                {teams?.length > 0 ? (
                  teams.map((team, index) => (
                    <ListItem key={index} disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => handleTeamToggle(team.name)}
                        sx={{
                          borderRadius: 1,
                          backgroundColor: tempSelectedTeams.includes(team.name)
                            ? alpha(theme.palette.primary.main, 0.08)
                            : "transparent",
                          "&:hover": {
                            backgroundColor: tempSelectedTeams.includes(
                              team.name,
                            )
                              ? alpha(theme.palette.primary.main, 0.12)
                              : alpha(theme.palette.primary.main, 0.1),
                          },
                        }}
                      >
                        <Checkbox
                          checked={tempSelectedTeams.includes(team.name)}
                          size="small"
                          sx={{
                            mr: 1,
                            color: theme.palette.primary.main,
                            "&.Mui-checked": {
                              color: theme.palette.primary.main,
                            },
                          }}
                        />
                        <ListItemText
                          primary={
                            <Typography
                              variant="body2"
                              fontWeight={500}
                              sx={{ color: theme.palette.text.primary }}
                            >
                              {team.name}
                            </Typography>
                          }
                          secondary={
                            team.description ? (
                              <Typography
                                variant="caption"
                                sx={{ color: theme.palette.text.secondary }}
                              >
                                {team.description}
                              </Typography>
                            ) : null
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))
                ) : (
                  <Box
                    sx={{
                      py: 6,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 2,
                    }}
                  >
                    <PeopleOutline
                      sx={{ fontSize: 48, color: theme.palette.text.disabled }}
                    />
                    <Typography
                      color="text.secondary"
                      align="center"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      No teams available
                    </Typography>
                  </Box>
                )}
              </List>
            </DialogContent>

            <DialogActions
              sx={{
                borderTop: `1px solid ${theme.palette.divider}`,
                p: 2.5,
                gap: 1.5,
              }}
            >
              <Button
                onClick={handleTeamSelectionCancel}
                variant="outlined"
                size="medium"
                sx={{
                  borderRadius: "0.5rem",
                  textTransform: "none",
                  fontWeight: 500,
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.primary,
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleTeamSelectionConfirm}
                variant="contained"
                size="medium"
                disabled={tempSelectedTeams.length === 0}
                sx={{
                  borderRadius: "0.5rem",
                  textTransform: "none",
                  fontWeight: 500,
                  backgroundColor: theme.palette.primary.main,
                  color: getButtonTextColor(),
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                  "&.Mui-disabled": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.3),
                    color: alpha(getButtonTextColor(), 0.5),
                  },
                }}
              >
                Confirm{" "}
                {tempSelectedTeams.length > 0 &&
                  `(${tempSelectedTeams.length})`}
              </Button>
            </DialogActions>
          </Dialog>

          {/* FILES AND MACHINES GRID - Only show files for automation mode */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {!isRegressionMode && (
              <div
                className={`border rounded-lg p-4 shadow-sm ${isDarkMode ? "border-gray-700 bg-[#2b2b2b]" : "border-gray-200 bg-white"}`}
              >
                <h3
                  className={`flex items-center text-base font-semibold mb-3 ${isDarkMode ? "text-white" : "text-gray-700"}`}
                >
                  <FiFileText
                    className="mr-2"
                    style={{ color: theme.palette.primary.main }}
                  />{" "}
                  Automation Files
                  {filesLoading && (
                    <span className="ml-3 text-xs animate-pulse">
                      Loading...
                    </span>
                  )}
                </h3>

                <div className="space-y-2">
                  {fileTypeList.map((type) => {
                    const block = automationFilesData[type];
                    const selected = block?.value;

                    return (
                      <div
                        key={type}
                        className={`p-2 rounded-lg border flex items-center`}
                        style={{
                          borderColor: selected
                            ? theme.palette.primary.main
                            : theme.palette.divider,
                          backgroundColor: selected
                            ? alpha(
                                theme.palette.primary.main,
                                isDarkMode ? 0.1 : 0.05,
                              )
                            : undefined,
                        }}
                      >
                        <div className="w-20 flex-shrink-0">
                          <span
                            className={`font-bold uppercase text-xs ${isDarkMode ? "text-gray-300" : "text-gray-600"} flex items-center`}
                          >
                            {type}
                          </span>
                        </div>

                        <div className="flex-1 min-w-0">
                          {selected ? (
                            <div className="flex justify-between items-center text-xs">
                              <span
                                className={`font-mono text-xs truncate mr-2 p-0.5 rounded border ${isDarkMode ? "border-gray-600 bg-[#3a3a3a] text-white" : "border-gray-300 bg-white text-black"} flex-1`}
                              >
                                {selected.customId}
                              </span>
                            </div>
                          ) : (
                            <span
                              className={`italic text-xs ${isDarkMode ? "text-red-400" : "text-red-500"}`}
                            >
                              Missing File
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div
              className={`border rounded-lg p-4 shadow-sm ${isDarkMode ? "border-gray-700 bg-[#2b2b2b]" : "border-gray-200 bg-white"}`}
            >
              <div className="flex justify-between items-center mb-3">
                <h3
                  className={`flex items-center text-base font-semibold ${isDarkMode ? "text-white" : "text-gray-700"}`}
                >
                  <FiServer
                    className="mr-2"
                    style={{ color: theme.palette.primary.main }}
                  />{" "}
                  Execution Machines
                </h3>
                <FiRefreshCw
                  onClick={fetchMachines}
                  className={`cursor-pointer hover:opacity-80 transition-colors ${loading ? "animate-spin" : ""} ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  style={{ color: theme.palette.primary.main }}
                  title="Refresh Machine List"
                />
              </div>

              <div className="grid grid-cols-1 gap-2 max-h-72 overflow-y-auto p-1 -m-1">
                {machines.map((machine) => {
                  const selected =
                    selectedMachine?.sessionId === machine.sessionId;
                  return (
                    <div
                      key={machine.sessionId}
                      onClick={() => setSelectedMachine(machine)}
                      className={`p-3 border rounded-lg shadow-sm cursor-pointer transition-all duration-200`}
                      style={{
                        borderColor: selected
                          ? theme.palette.primary.main
                          : theme.palette.divider,
                        backgroundColor: selected
                          ? alpha(
                              theme.palette.primary.main,
                              isDarkMode ? 0.2 : 0.05,
                            )
                          : undefined,
                        boxShadow: selected
                          ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)}`
                          : undefined,
                      }}
                    >
                      <p
                        className={`font-bold text-sm ${isDarkMode ? "text-white" : "text-gray-800"}`}
                      >
                        {machine.machineName}
                      </p>
                      <p
                        className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        ID: {machine.sessionId?.substring(0, 8) || "N/A"}...
                      </p>
                    </div>
                  );
                })}

                {machines.length === 0 && (
                  <p
                    className={`italic text-center py-4 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-400"}`}
                  >
                    No machines found. Click refresh to check again.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* BOUNDARY CONDITIONS SECTION */}
          <div
            className={`border rounded-lg p-4 shadow-sm ${
              isDarkMode
                ? "border-gray-700 bg-[#2b2b2b]"
                : "border-gray-200 bg-white"
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <h3
                className={`flex items-center text-base font-semibold ${
                  isDarkMode ? "text-white" : "text-gray-700"
                }`}
              >
                <FiGrid
                  className="mr-2"
                  style={{ color: theme.palette.primary.main }}
                />{" "}
                Boundary Conditions
                {(fieldNamesLoading || categoriesLoading) && (
                  <span className="ml-2 text-xs animate-pulse" style={{ color: theme.palette.primary.main }}>
                    Loading...
                  </span>
                )}
              </h3>

              {/* Toggle Switch */}
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={isRegressionMode ? enableRegressionBoundary : enableBoundaryConditions}
                  onChange={(e) => {
                    if (isRegressionMode) {
                      setEnableRegressionBoundary(e.target.checked);
                    } else {
                      setEnableBoundaryConditions(e.target.checked);
                    }
                  }}
                />
                <div
                  className={`w-11 h-6 rounded-full transition-all duration-300 ease-in-out
                    ${(isRegressionMode ? enableRegressionBoundary : enableBoundaryConditions) 
                      ? 'bg-gradient-to-r from-primary-500 to-primary-600' 
                      : 'bg-gray-400'
                    } peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300`}
                  style={{
                    background: (isRegressionMode ? enableRegressionBoundary : enableBoundaryConditions)
                      ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
                      : '#9ca3af'
                  }}
                ></div>
                <div
                  className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-all duration-300 ease-in-out
                    ${(isRegressionMode ? enableRegressionBoundary : enableBoundaryConditions) 
                      ? 'translate-x-5 shadow-md' 
                      : 'translate-x-0'
                    }`}
                ></div>
                <span
                  className={`ml-3 text-sm font-medium ${
                    isDarkMode ? "text-white" : "text-gray-700"
                  }`}
                >
                  {(isRegressionMode ? enableRegressionBoundary : enableBoundaryConditions) ? "Enabled" : "Disabled"}
                </span>
              </label>
            </div>

            {/* Different content based on mode */}
            {isRegressionMode ? (
              /* REGRESSION MODE - Just show info message when enabled */
              enableRegressionBoundary && (
                <div className="mt-4 p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                  <div className="flex items-start gap-3">
                    <Info sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                    <div>
                      <p className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
                        Boundary Conditions Pre-configured in Plan
                      </p>
                      <p className="text-sm text-blue-600 dark:text-blue-400">
                        Boundary conditions are already configured in the regression plan. 
                        Toggle the switch above to enable or disable them during execution.
                      </p>
                      {fieldNames.length > 0 && (
                        <div className="mt-3">
                          <p className="text-xs text-blue-600 dark:text-blue-400 font-medium mb-1">
                            Configured fields in this plan:
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {fieldNames.map((field, idx) => (
                              <Chip
                                key={idx}
                                label={field}
                                size="small"
                                sx={{
                                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                  color: theme.palette.primary.main,
                                  fontSize: "0.7rem",
                                  height: "24px",
                                }}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            ) : (
              /* AUTOMATION MODE (Single Test Case) - Show full Boundary Matrix for selection */
              (enableBoundaryConditions && (
                <div className="mt-4">
                  {fieldNames.length === 0 && !fieldNamesLoading ? (
                    <div
                      className={`p-4 text-center rounded-lg ${
                        isDarkMode ? "bg-gray-800" : "bg-gray-50"
                      }`}
                    >
                      <p
                        className={`text-sm ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        No fields available for boundary testing.
                      </p>
                      <p
                        className={`text-xs mt-1 ${
                          isDarkMode ? "text-gray-500" : "text-gray-500"
                        }`}
                      >
                        Please ensure the script file has Scenario Outline with Examples table.
                      </p>
                    </div>
                  ) : (
                    <BoundaryMatrix
                      fields={fieldNames}
                      categories={boundaryCategories}
                      selectedMatrix={currentMatrix}
                      onMatrixChange={setCurrentMatrix}
                      loading={fieldNamesLoading || categoriesLoading}
                    />
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* MODAL FOOTER */}
        <div
          className={`p-4 border-t flex justify-end gap-3 sticky bottom-0 ${
            isDarkMode
              ? "bg-[#1f1f1f] border-gray-700"
              : "bg-gray-50 border-gray-200"
          }`}
        >
          <Button
            onClick={() => {
              if (setShowExecuteModal) {
                setShowExecuteModal({ visible: false });
              }
              if (onClose) {
                onClose();
              }
            }}
            variant="contained"
            sx={{
              backgroundColor: theme.palette.error.main,
              color: getButtonTextColor(),
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "10px",
              px: 3,
              py: 1,
              "&:hover": {
                backgroundColor: theme.palette.error.dark,
              },
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleExecution}
            variant="contained"
            disabled={isExecuteDisabled}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              height: 40,
              px: 3,
              backgroundColor: theme.palette.primary.main,
              color: getButtonTextColor(),
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },
              "&.Mui-disabled": {
                backgroundColor: alpha(theme.palette.primary.main, 0.3),
                color: alpha(getButtonTextColor(), 0.5),
              },
            }}
          >
            {(isRegressionMode && enableRegressionBoundary) 
              ? "Execute with Boundary Conditions" 
              : isRegressionMode 
                ? "Execute Regression" 
                : enableBoundaryConditions 
                  ? "Execute with Boundary Conditions" 
                  : "Execute Automation"}
          </Button>
        </div>
      </div>
    </div>
  );
}